"use client";
import { motion, Variants } from "framer-motion";
import { Container, SectionSubHeading, SectionTitle } from "@/components";

interface TeamExpertiseProps {
  name: string;
  expertise: string[];
}

const card: Variants = {
  hidden: { opacity: 0, y: 28 },
  visible: (i: number) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.55, delay: i * 0.1, ease: [0.22, 1, 0.36, 1] },
  }),
};

// The disciplines this member owns — pulled straight from `expertise`, rendered
// as bold neo-brutalist cards so the list reads as capability, not tags.
const TeamExpertise = ({ name, expertise }: TeamExpertiseProps) => {
  const first = name.split(" ")[0];

  return (
    <section className="bg-background py-14 md:py-20">
      <Container className="px-4 lg:px-10">
        <div className="space-y-3">
          <SectionSubHeading subtitle="What He Owns" className="text-primary" />
          <SectionTitle
            title={
              <>
                {first.toUpperCase()}&rsquo;S <br /> WHEELHOUSE.
              </>
            }
          />
        </div>

        <div className="mt-14 grid gap-6 md:mt-20 md:grid-cols-3">
          {expertise.map((item, i) => (
            <motion.div
              key={item}
              variants={card}
              custom={i}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, amount: 0.4 }}
              className="group relative overflow-hidden rounded-2xl border-2 border-black bg-secondary p-7 shadow-[6px_6px_0_0_rgba(0,0,0,1)] transition-transform duration-300 hover:-translate-y-1"
            >
              <div
                aria-hidden
                className="pointer-events-none absolute inset-0 opacity-0 transition-opacity duration-300 group-hover:opacity-100"
                style={{
                  background:
                    "radial-gradient(70% 60% at 50% 0%, var(--color-primary), transparent 70%)",
                }}
              />
              <span className="relative font-black leading-none tracking-tighter text-4xl text-primary">
                {String(i + 1).padStart(2, "0")}
              </span>
              <h3 className="relative mt-6 font-black uppercase leading-tight tracking-tight text-xl md:text-2xl">
                {item}
              </h3>
            </motion.div>
          ))}
        </div>
      </Container>
    </section>
  );
};

export default TeamExpertise;
